// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  MembershipLocation,
  MembershipReportData,
} from '@/membership/api/membership-types';
import { DateTime } from 'luxon';

/** The date a day index falls on, which is the only place dates are built. */
export function toDayDate(startOn: DateTime, dayIndex: number): DateTime {
  return startOn.plus({ days: dayIndex });
}

/** Whether the asset at that index counted on that day. */
export function isMemberOnDay(
  report: MembershipReportData,
  assetIndex: number,
  dayIndex: number
): boolean {
  const byte =
    report.residency[assetIndex * report.bytesPerMask + (dayIndex >> 3)];
  return (byte & (1 << (7 - (dayIndex & 7)))) !== 0;
}

/** One member on the scrubbed day, with where it stood on it. */
export interface MemberOnDay {
  key: string;
  serialNumber: string;
  itemSku: string;
  locationName: string;
}

/**
 * The members standing on one day, in the SKUs the chips leave showing.
 *
 * A bit test per asset rather than a walk of every stretch, so the cost follows
 * the number of members and not how often any of them moved.
 */
export function toMembersOnDay(
  report: MembershipReportData,
  dayIndex: number,
  itemSkus: string[]
): MemberOnDay[] {
  const shown = new Set(itemSkus);
  const out: MemberOnDay[] = [];
  report.assets.forEach((asset, index) => {
    if (!shown.has(asset.itemSku)) return;
    if (!isMemberOnDay(report, index, dayIndex)) return;
    out.push({
      key: asset.key,
      serialNumber: asset.serialNumber,
      itemSku: asset.itemSku,
      locationName: toLocationOnDay(asset.locations, dayIndex),
    });
  });
  return out.sort((left, right) =>
    left.serialNumber.localeCompare(right.serialNumber)
  );
}

/**
 * Where an asset stood on one day. Locations are recorded when they change, so
 * the answer is the last one that started on or before the day.
 */
export function toLocationOnDay(
  locations: MembershipLocation[],
  dayIndex: number
): string {
  let out = '';
  for (const one of locations) {
    if (one.fromDay > dayIndex) break;
    out = one.name;
  }
  return out;
}

/** One SKU and how many of its assets the report holds. */
export interface SkuTotal {
  itemSku: string;
  memberCount: number;
}

/** Every SKU in the report with its member count, ordered by SKU. */
export function toSkuTotals(report: MembershipReportData): SkuTotal[] {
  const counts = new Map<string, number>();
  report.assets.forEach((asset) => {
    counts.set(asset.itemSku, (counts.get(asset.itemSku) ?? 0) + 1);
  });
  return report.skus.map((sku) => ({
    itemSku: sku.itemSku,
    memberCount: counts.get(sku.itemSku) ?? 0,
  }));
}

/** One SKU's daily counts, aligned to the window's days. */
export interface SkuSeries {
  itemSku: string;
  counts: number[];
}

/**
 * The counts the chart stacks. The server reduces these, so a SKU the chips hid
 * is dropped rather than counted as zero and nothing is derived here.
 */
export function toDailyCountsBySku(
  report: MembershipReportData,
  itemSkus: string[]
): SkuSeries[] {
  const shown = new Set(itemSkus);
  return report.skus
    .filter((sku) => shown.has(sku.itemSku))
    .map((sku) => ({ itemSku: sku.itemSku, counts: sku.counts }));
}

/** One SKU's daily movements, aligned to the window's days. */
export interface SkuDelta {
  itemSku: string;
  joined: number[];
  left: number[];
}

/**
 * What moved each day, per SKU. The server sends these keyed by day because
 * most days move nothing, so they are spread into the window here.
 */
export function toDailyDeltasBySku(
  report: MembershipReportData,
  itemSkus: string[]
): SkuDelta[] {
  const shown = new Set(itemSkus);
  return report.skus
    .filter((sku) => shown.has(sku.itemSku))
    .map((sku) => ({
      itemSku: sku.itemSku,
      joined: toDenseDays(sku.joined, report.dayCount),
      left: toDenseDays(sku.left, report.dayCount),
    }));
}

function toDenseDays(
  byDay: Record<string, number>,
  dayCount: number
): number[] {
  const out = Array.from({ length: dayCount }, () => 0);
  Object.entries(byDay).forEach(([day, count]) => {
    const index = Number(day);
    if (index >= 0 && index < dayCount) out[index] = count;
  });
  return out;
}

/**
 * What moved on one day: what was there the day before, what joined and left
 * that day, and what is there now.
 *
 * The four numbers reconcile, so starting plus joined less left is ending.
 */
export interface MembershipRollforward {
  starting: number;
  joined: number;
  left: number;
  ending: number;
}

/** The rollforward for one day over the SKUs the chips leave showing. */
export function toRollforward(
  report: MembershipReportData,
  dayIndex: number,
  itemSkus: string[]
): MembershipRollforward {
  const shown = new Set(itemSkus);
  const out: MembershipRollforward = {
    starting: 0,
    joined: 0,
    left: 0,
    ending: 0,
  };
  report.skus.forEach((sku) => {
    if (!shown.has(sku.itemSku)) return;
    out.ending += sku.counts[dayIndex] ?? 0;
    out.starting += dayIndex > 0 ? (sku.counts[dayIndex - 1] ?? 0) : 0;
    out.joined += sku.joined[String(dayIndex)] ?? 0;
    out.left += sku.left[String(dayIndex)] ?? 0;
  });
  return out;
}
